import React, { useState, useEffect } from "react";
import axiosInstance from '../services/axiosInstance';
import GlobalTable from "../components/GlobalTable";
import { handleApiError, handleApiSuccess } from "../components/apiErrorHandler";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { AutoComplete } from "primereact/autocomplete";

const ProductAttributeConfig = () => {
    const [attribute, setAttribute] = useState({
        name: "",
        attribute_type: "",
        product: "",
        organization: "b835b027-72ba-4c99-af9c-c8c6c1e1c732",
        list_values: [],
    });
    const [attributes, setAttributes] = useState([]);
    const [productsq, setProductsq] = useState([]);
    const [listValue, setListValue] = useState("");
    const [editingLovAttribute, setEditingLovAttribute] = useState(null);
    const [dialogListValue, setDialogListValue] = useState("");
    const [dialogVisible, setDialogVisible] = useState(false);
    const [loading, setLoading] = useState(true);   
    const attributeTypes = [
        { label: "Text", value: "text" },
        { label: "List of Values", value: "lov" },
        { label: "Date", value: "date" },
    ];

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const attributeResult = await axiosInstance.get("/api/product-attributes/");
                setAttributes(attributeResult.data);
                setLoading(false);
            } catch (error) {
                handleApiError(error);
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleChange = (e, field) => {
        const value = e.target ? e.target.value : e.value;
        setAttribute((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleAddListValue = (e) => {
        e.preventDefault();
        if (!listValue.trim()) {
            handleApiError({Error:"LOV value cannot be empty."});
            return;
        }
        if (attribute.list_values.includes(listValue)) {
            handleApiError({Error:"LOV value already exists."});
            return;
        }
        setAttribute((prev) => ({
            ...prev,
            list_values: [...prev.list_values, listValue],
        }));
        setListValue("");
    };

    const handleRemoveListValue = (e, index) => {
        e.preventDefault(); // Prevent the default form submission
        setAttribute((prev) => ({
            ...prev,
            list_values: prev.list_values.filter((_, i) => i !== index),
        }));
    };

    const handleDialogAddListValue = () => {
        if (!dialogListValue.trim()) {
            handleApiError({Error:"LOV value cannot be empty."});
            return;
        }
        if (editingLovAttribute.lov_values.some((lov) => lov.value === dialogListValue)) {
            handleApiError({Error:"LOV value already exists."});
            return;
        }
        setEditingLovAttribute((prev) => ({
            ...prev,
            lov_values: [...prev.lov_values, { value: dialogListValue }],
        }));
        setDialogListValue("");
    };

    const handleDialogRemoveListValue = (index) => {
        setEditingLovAttribute((prev) => ({
            ...prev,
            lov_values: prev.lov_values.filter((_, i) => i !== index),
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const formattedAttribute = {
            ...attribute,
            lov_values: attribute.attribute_type === "lov"? attribute.list_values.map(value => ({ value })) : [],
        };
        
        try {
            const result = await axiosInstance.post("/api/product-attributes/", formattedAttribute);
            setAttributes([...attributes, result.data]);
            setAttribute({ name: "", attribute_type: "", product: "", organization: "b835b027-72ba-4c99-af9c-c8c6c1e1c732", list_values: [] });
            handleApiSuccess(`Attribute created: ${result.data.name}`);
        } catch (error) {
            handleApiError(error);
        }
    };

    const handleRowEditComplete = async (e) => {
        const { newData, index } = e;
        try {
            const updatedAttribute = await axiosInstance.put(
                `/api/product-attributes/${newData.id}/`,
                newData
            );
            const updatedAttributes = [...attributes];
            updatedAttributes[index] = updatedAttribute.data;
            setAttributes(updatedAttributes);
            handleApiSuccess("Attribute updated successfully!");
        } catch (error) {
            handleApiError(error);
        }
    };

    const handleSaveLovValues = async () => {
        try {
            const updatedAttribute = await axiosInstance.put(
                `/api/product-attributes/${editingLovAttribute.id}/`,
                editingLovAttribute
            );
            setAttributes((prev) =>
                prev.map((attr) => (attr.id === updatedAttribute.data.id ? updatedAttribute.data : attr))
            );
            setEditingLovAttribute(null); // Close modal
            handleApiSuccess("LOV values updated successfully!");
        } catch (error) {
            handleApiError(error);
        }
    };

    const handleDeleteAttribute = async (attribute) => {
        const confirmDelete = window.confirm(`Are you sure you want to delete the attribute "${attribute.name}"?`);
        if (!confirmDelete) return;
    
        try {
            await axiosInstance.delete(`/api/product-attributes/${attribute.id}/`);
            setAttributes((prev) => prev.filter((attr) => attr.id !== attribute.id));
            handleApiSuccess(`Attribute "${attribute.name}" has been deleted.`);
        } catch (error) {
            handleApiError(error);
        }
    };
    
    const columns = [
        { Header: "Name", accessor: "name", sortable: true, filtered: true },
        {
            Header: "Type",
            accessor: "attribute_type",
            sortable: true,
            readOnly: true,
            filtered:true,
            bodyTemplate: (rowData) =>
                attributeTypes.find((type) => type.value === rowData.attribute_type)?.label || rowData.attribute_type,
        },
        {
            Header: "Product",
            accessor: "name",
            sortable: true,
            filtered:true,
            readOnly: true,
        },
        {
            Header: "List Values",
            accessor: "lov_values",
            sortable: true,
            readOnly: true,
            bodyTemplate: (rowData) => (
                <>
                    {rowData.attribute_type === "lov" ? (
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                            <span>{rowData.lov_values?.map((lov) => lov.value).join(", ")}</span>
                            <Button
                                icon="pi pi-pencil"
                                className="p-button-text"
                                onClick={() => setEditingLovAttribute(rowData)}
                                tooltip="Edit List Values"
                                tooltipOptions={{ position: "top" }}
                            />
                        </div>
                    ) : (
                        <span></span> // Display a dash or alternative for non-LOV attributes
                    )}
                </>
            ),
        }
    ];

    const headerAction = (
        <Button type="button" icon="pi pi-plus" label=" Add Attribute" outlined onClick={() => setDialogVisible(true)} className="w-full sm:w-auto" />
    );

    return (
        <div>
            <div className="containers">
                <GlobalTable
                    columns={columns}
                    data={attributes}
                    tabletitle="Product Attributes"
                    handleDeleteRecord={handleDeleteAttribute}
                    onRowEditComplete={handleRowEditComplete}
                    showEditColumn={false}
                    showEditDeleteColumn={true}
                    headerAction={headerAction}
                    loading={loading}
                />
            </div>
            <Dialog
                header="Add Product Attributes"
                visible={dialogVisible}
                onHide={() => setDialogVisible(false)}
                modal
                style={{ width: '32rem', borderRadius: '12px', padding: '.5rem'}} 
                breakpoints={{ '960px': '75vw', '641px': '90vw' }}
                className="p-fluid"
            >
            <form id="dialogForm" onSubmit={handleSubmit}>
            <div style={{ borderRadius: '12px', padding: '1rem' }}>
                <div className="field" >
                    <label htmlFor="attribute_name">Attribute Name</label>
                    <InputText
                        id="attribute_name"
                        value={attribute.name}
                        onChange={(e) => handleChange(e, "name")}
                        placeholder="Enter Attribute Name"
                        required
                    />
                </div>
                <div className="field">
                    <label htmlFor="attribute_type">Attribute Type</label>
                    <Dropdown
                        id="attribute_type"
                        value={attribute.attribute_type}
                        options={attributeTypes}
                        onChange={(e) => handleChange(e, "attribute_type")}
                        placeholder="Select Type"
                        showClear
                        required
                    />
                </div>
                <div className="field">
                    <label htmlFor="product">Product</label>
                    <AutoComplete
                        value={attribute.product_name || ""}
                        suggestions={productsq}
                        completeMethod={(e) => {
                            const query = e.query;
                            axiosInstance
                                .get(`/api/productsq?query=${query}`)
                                .then((res) => {
                                    const processedSuggestions = res.data.map((product) => ({
                                        ...product,
                                        displayLabel: product.name,
                                    }));
                                    setProductsq(processedSuggestions);
                                })
                                .catch((error) => handleApiError(error));
                        }}
                        field="name"
                        onChange={(e) => {
                            if (e.value && typeof e.value === "object") {
                                handleChange({ target: { value: e.value.id } }, "product");
                                handleChange({ target: { value: e.value.name } }, "name");
                            } else {
                                handleChange({ target: { value: e.value } }, "name");
                                handleChange({ target: { value: null } }, "product");
                            }
                        }}
                        placeholder="Type in Product"
                        required
                    />
                </div>
                {attribute.attribute_type === "lov" && (
                <div className="field col">
                    <div style={{ display: "flex", gap: "2rem" }}>
                        <InputText
                            value={listValue}
                            onChange={(e) => setListValue(e.target.value)}
                            placeholder="Add List Value"
                        />
                        <Button label="Add" onClick={handleAddListValue} style={{ maxWidth:"100px"}}/>
                    </div>
                    <ul>
                        {attribute.list_values.map((value, index) => (
                            <li key={index} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                {value}
                                <Button
                                    icon="pi pi-times"
                                    className="p-button-text p-button-danger"
                                    onClick={(e) => handleRemoveListValue(e, index)} // Pass the event and index
                                />
                            </li>
                        ))}
                    </ul>
                </div>
                        )}
                        </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                        <Button label="Save" icon="pi pi-check" form="dialogForm" type='submit' style={{borderRadius:"6px", maxWidth: "120px"}} />
                        <Button label="Cancel" icon="pi pi-times" outlined onClick={(e) => {setDialogVisible(false); e.preventDefault();}}  style={{borderRadius:"6px", maxWidth: "120px"}} />
                    </div> 
                </form>
                </Dialog>
            
            <Dialog
                header="Edit List of Values"
                visible={!!editingLovAttribute}
                style={{ width: "25vw" }}
                breakpoints={{ '960px': '75vw', '641px': '100vw' }}
                onHide={() => setEditingLovAttribute(null)}
            >
                <div>
                    <ul>
                        {editingLovAttribute?.lov_values.map((lov, index) => (
                            <li key={index} style={{ display: "flex", justifyContent: "space-between" }}>
                                {lov.value}
                                <Button
                                    icon="pi pi-times"
                                    className="p-button-text p-button-danger"
                                    onClick={() => handleDialogRemoveListValue(index)}
                                />
                            </li>
                        ))}
                    </ul>
                    <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem" }}>
                        <InputText
                            value={dialogListValue}
                            onChange={(e) => setDialogListValue(e.target.value)}
                            placeholder="Add List Value"
                        />
                        <Button label="Add" onClick={handleDialogAddListValue} />
                    </div>
                    <Button
                        label="Update Values"
                        style={{ marginTop: "1rem" }}
                        onClick={handleSaveLovValues}
                    />
                </div>
            </Dialog>
        </div>
    );
};

export default ProductAttributeConfig;
